import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { SearchContextProvider, useSearchContext } from "../../context/searchContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { getProjectStatusValues, SearchProject } from "../../http/types/projects";
import { useProjectsApi } from "../../http/useProjects";
import { Paginated, PaginationQuery } from "../../types";
import { formatTimeStamp } from "../../utils";
import SvgAddUsers from "../icons/AddUsers";
import SvgEdit from "../icons/Edit";
import SvgVisibility from "../icons/Visibility";
import FlexContent from "../ui/FlexContent";
import Header from "../ui/Header";
import { getDeleteAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import SearchBar from "../ui/SearchBar";
import StatusBadge from "../ui/StatusBadge";
import Table from "../ui/Table";
import CreateProjectButton from "./CreateProjectButton";
import EditProjectStatus from "./EditProjectStatus";
import ProjectSharingAccessesDialog from "./ProjectSharingAccessesDialog";

const Projects: FC = () => {
  return (
    <SearchContextProvider>
      <ProjectsContent />
    </SearchContextProvider>
  );
};

const ProjectsContent: FC = () => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const [projectToDelete, setProjectToDelete] = useState<SearchProject>();
  const projectsApi = useProjectsApi();
  const { actions, setActions } = useSearchContext();

  const debouncedValue = useDebounce(filters.state.q);
  const searchPlaceholder = i18n.translation.projects.search;

  const stati = getProjectStatusValues(i18n.translation);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const [projects, setProjects] = useState<Paginated<SearchProject>>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [projectToManageSharing, setProjectToManageSharing] = useState<SearchProject>();
  const { data, error } = projectsApi.useMyProjects(query, aborter.current.signal);

  useEffect(() => {
    setProjects(data);
  }, [data?.items]);

  useEffect(() => {
    setActions(
      <>
        <SearchBar
          placeholder={searchPlaceholder}
          value={filters.state.q ?? ""}
          onChange={(term) => {
            aborter.current.abort();
            aborter.current = new AbortController();
            filters.setSearchTerm(term);
          }}
        />
        <CreateProjectButton onSubmit={(id) => navigate(`/projects/${id}`)} />
      </>,
    );

    return () => {
      setActions(null);
    };
  }, [filters.state.q, navigate, searchPlaceholder, setActions]);

  return (
    <>
      <Header
        title={i18n.translation.projects.plural}
        error={error}
        path={{ display: i18n.translation.projects.my }}
        buttons={actions}
      />

      <FlexContent padding>
        <Table<SearchProject>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left">{i18n.translation.common.name}</th>
              <th align="left">{i18n.translation.common.status}</th>
              <th align="left">{i18n.translation.common.location}</th>
              <th align="left">{i18n.translation.common.country}</th>
              <th align="left">{i18n.translation.common.lastModified}</th>
              <th></th>
            </tr>
          }
          items={projects?.items}
          renderRow={(item) => {
            return (
              <>
                <td>
                  <Link to={`/projects/${item.id}?tab=1`} onClick={(e) => e.stopPropagation()}>
                    {item.name}
                  </Link>
                </td>
                <td>
                  {item.canEdit ? (
                    <EditProjectStatus
                      project={item}
                      stati={stati}
                      onUpdate={(status) => {
                        if (projects) {
                          const optimisticData: Paginated<SearchProject> = {
                            items: projects.items.map((p) => ({
                              ...p,
                              status: p.id === item.id ? status : p.status,
                            })),
                            total: projects.total,
                          };
                          projectsApi.mutateMyProjects(query, optimisticData);
                          setProjects(optimisticData);
                        }
                      }}
                    />
                  ) : (
                    (() => {
                      const status = stati.find((x) => x.value === item.status);
                      return status && <StatusBadge color={status.color}>{status.display}</StatusBadge>;
                    })()
                  )}
                </td>
                <td>{item.city}</td>
                <td>{item.country}</td>
                <td>{formatTimeStamp(item.modifiedAt, auth.identity?.language)}</td>
              </>
            );
          }}
          onRowClick={(item) => navigate(`/projects/${item.id}`)}
          rowActions={(item) => [
            {
              title: item.canEdit ? i18n.translation.common.edit : i18n.translation.common.open,
              onClick: () => navigate(`/projects/${item.id}`),
              icon: item.canEdit ? SvgEdit : SvgVisibility,
            },
            {
              title: i18n.translation.projects.editSharings,
              onClick: () => item.author.id === auth.identity?.id && setProjectToManageSharing(item),
              icon: SvgAddUsers,
              isDisabled: item.author.id !== auth.identity?.id,
            },
            getDeleteAction(
              i18n,
              () => {
                item.author.id === auth.identity?.id && setProjectToDelete(item);
                return item.author.id !== auth.identity?.id;
              },
              true,
              item.author.id !== auth.identity?.id,
            ),
          ]}
          total={projects?.total}
          error={!!error}
        />
      </FlexContent>
      {!!projectToDelete && (
        <ModalDialog
          onClose={() => setProjectToDelete(undefined)}
          title={i18n.translation.projects.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              type: "secondary",
              onClick: () => setProjectToDelete(undefined),
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                projectsApi
                  .deleteProject(projectToDelete.id)
                  .then(() => setProjectToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.projects.questions.delete}
        </ModalDialog>
      )}
      {!!projectToManageSharing && (
        <ProjectSharingAccessesDialog
          projectId={projectToManageSharing.id}
          projectName={projectToManageSharing.name}
          onClose={() => setProjectToManageSharing(undefined)}
        />
      )}
    </>
  );
};

export default Projects;
